import React, { useState } from 'react';
import styled from 'styled-components';
import { signInWithFirebase, resendVerificationEmail } from '../../firebase/firebase';
import { login } from '../../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  width: 90%;
  max-width: 450px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
`;

const Logo = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  text-align: center;
  margin-bottom: 30px;
  color: #1a2a3a;
  
  span {
    color: #c4a484;
    font-size: 11px;
    display: block;
    letter-spacing: 3px;
    margin-top: 5px;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  color: #1a2a3a;
  font-weight: 300;
  font-size: 24px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  padding-right: 45px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #c4a484;
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 12px;
  bottom: 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #666;
  padding: 0;
  
  &:hover {
    color: #c4a484;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: #c4a484;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #b89474;
  }
`;

const SwitchText = styled.p`
  text-align: center;
  margin-top: 20px;
  color: #666;
  font-size: 14px;
  
  span {
    color: #c4a484;
    cursor: pointer;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 5px;
  text-align: center;
`;

const VerificationNotice = styled.div`
  background: #fff3cd;
  border: 1px solid #ffecb5;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: center;
  
  p {
    margin: 5px 0;
    font-size: 13px;
    color: #856404;
  }
  
  button {
    background: none;
    border: none;
    color: #c4a484;
    cursor: pointer;
    font-weight: 600;
    margin-top: 8px;
  }
`;

const LoginModal = ({ onClose, onLogin, onSwitchToSignup }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleResendVerification = async () => {
    const result = await resendVerificationEmail();
    if (result.success) {
      setResendMessage('✅ Verification email resent! Check your inbox.');
      setTimeout(() => setResendMessage(''), 5000);
    } else {
      setResendMessage('❌ ' + result.error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);
    
    try {
      // STEP 1: Authenticate with Firebase
      const firebaseResult = await signInWithFirebase(email, password);
      
      if (!firebaseResult.success) {
        if (firebaseResult.needsVerification) {
          setNeedsVerification(true);
          setError(firebaseResult.error);
        } else {
          setError(firebaseResult.error || 'Login failed');
        }
        setLoading(false);
        return;
      }
      
      console.log('✅ Firebase authenticated:', firebaseResult.firebaseUid);
      
      // STEP 2: Get user profile from Neon database
      const dbResult = await login({ 
        email: email, 
        firebaseUid: firebaseResult.firebaseUid 
      });
      
      if (dbResult.success) {
        onLogin(dbResult.user);
        onClose();
      } else {
        setError(dbResult.error || 'Failed to load user data');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>✕</CloseButton>
        <Logo>
          CEY-VALORA
          <span>ATLANTA • GEORGIA</span>
        </Logo>
        <Title>Welcome Back</Title>
        
        {needsVerification && (
          <VerificationNotice>
            <p>📧 Please verify your email before logging in.</p>
            <p>Check your inbox (and spam folder) for the verification link.</p>
            <button onClick={handleResendVerification}>Resend verification email</button>
            {resendMessage && <p style={{ marginTop: '8px', color: '#155724' }}>{resendMessage}</p>}
          </VerificationNotice>
        )}
        
        <form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Password</Label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            <EyeButton type="button" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </EyeButton>
          </InputGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </Button>
          <SwitchText>
            Don't have an account? <span onClick={onSwitchToSignup}>Sign Up</span>
          </SwitchText>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default LoginModal;
